import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CloseIcon from '@mui/icons-material/Close';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import MailIcon from '@mui/icons-material/MailOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ReportIcon from '@mui/icons-material/Report';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState, useEffect } from 'react';
import {
	CopyToClipboardIcon,
	DataGrid,
	FilterAsyncAutoComplete,
	FilterAutoCompleteById,
	FilterSearch,
	FilterSelect,
	RoleChip,
	Spinner,
} from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import MergeUsersDialog from 'src/components/MergeUsersDialog';
import ScanDialog from 'src/components/ScanDialog';
import { Badge } from '@repo/components';
import { PERMISSIONS, Role, User } from '@repo/shared/domain';
import { InviteForm } from '@repo/forms';
import { reportDownload, constructQuery } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import BadgeConfiguratorDialog from 'src/parts/users/BadgeConfiguratorDialog';
import { useRouteState } from 'src/hooks/useRouteState';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getUsers, getBlockedCount, getInReviewCount } from 'src/api/admin/users';
import { getRolesForAutocomplete, getInviteRoles } from 'src/api/admin/roles';
import { downloadReport } from 'src/api/admin/reports';
interface UsersSearch {
	sort?: string;
	page?: number;
	search?: string;
	groupIds?: string;
	roleIds?: string;
	roleStatus?: string;
	tab?: string;
}

export const Route = createFileRoute('/_admin/users/')({
	validateSearch: (search: Record<string, unknown>): UsersSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		search: search.search as string | undefined,
		groupIds: search.groupIds as string | undefined,
		roleIds: search.roleIds as string | undefined,
		roleStatus: search.roleStatus as string | undefined,
		tab: search.tab as string | undefined,
	}),
	component: UsersPage,
});

type TabId = 'active' | 'markedForReview' | 'blocked' | 'archived';

const downloadUsersReport = async (query: string) => {
	try {
		const blob = await downloadReport('users', query);
		if (blob) {
			reportDownload(blob);
		}
	} catch (error) {
		console.error('Failed to download report:', error);
	}
};

const columns = [
	{
		field: 'markedForReview',
		headerName: '',
		renderCell: (c: { row: User }) => c.row.markedForReview && <ReportIcon color="error" />,
		width: 5,
		sortable: false,
	},
	{
		field: 'firstName',
		headerName: 'First name',
		flex: 1,
	},
	{
		field: 'lastName',
		headerName: 'Last name',
		flex: 1,
	},
	{
		field: 'roles',
		headerName: 'Roles',
		flex: 1,
		renderCell: (c: { row: User; value: Role[] }) =>
			c.value.map((r) => (
				<RoleChip key={r.name} role={r} userMarkedForReview={c.row.markedForReview || c.row.status === 'blocked'} />
			)),
		sortable: false,
	},
	{
		field: 'groups',
		headerName: 'Companies',
		flex: 1,
		renderCell: (c: { value?: { name: string }[] }) => c.value?.map((r) => <Badge key={r.name}>{r.name}</Badge>),
		sortable: false,
	},
	{
		field: 'lastUserActivityAt',
		headerName: 'Last Activity',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'email',
		headerName: '',
		width: 10,
		renderCell: (c: { value?: string }) =>
			c.value && <CopyToClipboardIcon Icon={MailIcon} value={c.value} side="top" align="center" />,
		sortable: false,
	},
	{
		field: 'phone',
		headerName: '',
		width: 10,
		renderCell: (c: { value?: string }) =>
			c.value && <CopyToClipboardIcon Icon={PhoneIcon} value={c.value} side="top" align="center" />,
		sortable: false,
	},
];

// All possible role statuses can be found in the roles.status enum on our User model
const roleStatusTypes = [
	{ value: 'all', label: 'All' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'accepted', label: 'Accepted' },
	{ value: 'expired', label: 'Expired' },
	{ value: 'expiryWarning', label: 'Expiry Warning' },
	{ value: 'archived', label: 'Archived' },
];

function UsersPage() {
	const searchParams = Route.useSearch();
	const { sort, page, search, groupIds, roleIds, roleStatus, tab } = searchParams;
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { updateValue } = useLocalStorage<string>('usersBackUrl');
	useBreadcrumbLevel(0, 'Users');
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const featureFlags = config?.featureFlags || {};
	const defaultCountry = config?.defaultCountry || 'BE';
	const tenantSecret = config?.tenantSecret || '';
	const queryParams = {
		sort: sort || 'lastUserActivityAt:desc',
		page,
		search,
		groupIds,
		roleIds,
		roleStatus,
		tab,
	};
	const query = constructQuery(queryParams);

	const { data: usersData, isLoading: usersLoading } = useQuery({
		queryKey: ['users', query],
		queryFn: () => getUsers(query),
	});

	const { data: rolesData } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: () => getRolesForAutocomplete(),
	});

	const { data: inviteRolesData } = useQuery({
		queryKey: ['invite-roles'],
		queryFn: () => getInviteRoles(),
	});

	const { data: inReviewCountData } = useQuery({
		queryKey: ['in-review-count'],
		queryFn: () => getInReviewCount(),
	});

	const { data: blockedCountData } = useQuery({
		queryKey: ['blocked-count'],
		queryFn: () => getBlockedCount(),
	});

	const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState(false);
	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isScanDialogOpen, setIsScanDialogOpen] = useState(false);
	const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
	const [isSelectionMode, setIsSelectionMode] = useState(false);
	const [rowSelection, setRowSelection] = useState<Record<TabId, Record<number, true>>>({
		active: {},
		archived: {},
		blocked: {},
		markedForReview: {},
	});

	const tabs: Array<{ id: TabId; label: string; error?: string }> = [
		{
			id: 'active',
			label: 'Active',
		},
		{
			id: 'markedForReview',
			label: 'In Review',
			error: inReviewCountData?.count ? String(inReviewCountData.count) : undefined,
		},
		{
			id: 'blocked',
			label: 'Blocked',
			error: blockedCountData?.count ? String(blockedCountData.count) : undefined,
		},
		{
			id: 'archived',
			label: 'Archived',
		},
	];

	const { value: activeTab } = useRouteState({
		urlAccessor: undefined,
		defaultValue: 'active',
		onChange: undefined,
	});

	const data = useMemo(() => usersData || { data: [], count: 0, page: 0, pageSize: 25 }, [usersData]);
	const roles = rolesData || [];
	const inviteRoles = inviteRolesData || [];

	useEffect(() => {
		// Get the row indices selected for the current tab
		const currentTabSelection = rowSelection[activeTab as TabId] || {};

		// Map the selected indices back to User objects
		const usersForCurrentTab = Object.keys(currentTabSelection)
			.map((index) => data.data[parseInt(index)])
			.filter(Boolean); // Filter out any undefined values

		setSelectedUsers(usersForCurrentTab);
	}, [activeTab, rowSelection, data.data]);

	const handleTabChange = (newValue: string) => {
		updateQueryParams({ tab: newValue });
	};

	const downloadCSVReport = useCallback(async () => {
		const params = new URLSearchParams();
		if (sort) params.set('sort', sort);
		if (page) params.set('page', page);
		if (search) params.set('search', search);
		if (groupIds) params.set('groupIds', groupIds);
		if (roleIds) params.set('roleIds', roleIds);
		if (roleStatus) params.set('roleStatus', roleStatus);
		if (tab) params.set('tab', tab);
		downloadUsersReport(params.toString());
	}, [sort, page, search, groupIds, roleIds, roleStatus, tab]);

	const handleSort = (sortModel: { field: string; sort: 'asc' | 'desc' }[]) => {
		const firstSort = sortModel[0];
		if (!firstSort) {
			return;
		}

		updateQueryParams({ sort: sortToString(firstSort) });
	};

	const handleRowClick = ({ id }: { id: string }) => {
		updateValue(window.location.href);
		// Navigate to user detail page - using window.location as route may not exist yet
		navigate({ to: '/users/$userId', params: { userId: id } });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterData: {
		groupIds?: string[] | string;
		roleIds?: string[] | string;
		roleStatus?: string[] | string;
		search?: string;
	}) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const revalidateUsers = () => {
		queryClient.invalidateQueries({ queryKey: ['users'] });
		queryClient.invalidateQueries({ queryKey: ['in-review-count'] });
		queryClient.invalidateQueries({ queryKey: ['blocked-count'] });
	};

	const handleInvite = async (inviteData: Record<string, unknown>) => {
		const { body: user } = await request.post('/api/invites/users', inviteData);
		revalidateUsers();
		return user;
	};

	const handleOpenScanDialog = useCallback(() => {
		setIsScanDialogOpen(true);
	}, []);

	const handleCloseScanDialog = useCallback(() => {
		setIsScanDialogOpen(false);
	}, []);

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleCloseReadBadgeDialog = () => {
		setIsBadgeDialogOpen(false);
	};

	const handleOpenReadBadgeDialog = async () => {
		setIsBadgeDialogOpen(true);
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleScan = async (qrCode: string) => {
		const { body: user } = await request.get<undefined, User>(`/api/users/qr/${qrCode}`, { schema: undefined });
		if (user) {
			window.location.href = `/users/${user.id}`;
		}
	};

	const handleMergeUsers = () => {
		setIsSelectionMode(true);
		setRowSelection({
			active: {},
			archived: {},
			blocked: {},
			markedForReview: {},
		});
		setSelectedUsers([]);
	};

	const handleMergeSuccess = () => {
		revalidateUsers();
		setSelectedUsers([]);
		setIsSelectionMode(false);
		setRowSelection({
			active: {},
			archived: {},
			blocked: {},
			markedForReview: {},
		});
		setIsMergeDialogOpen(false);
	};

	const handleCancelSelection = () => {
		setIsSelectionMode(false);
		setSelectedUsers([]);
		setRowSelection({
			active: {},
			archived: {},
			blocked: {},
			markedForReview: {},
		});
		setIsMergeDialogOpen(false);
	};

	const handleRowSelectionChange = (newSelectedRows: User[]) => {
		setSelectedUsers(newSelectedRows);
		const newRowSelection: Record<TabId, Record<number, true>> = {
			...rowSelection,
			[activeTab as TabId]: {},
		};
		newSelectedRows.forEach((selectedRow) => {
			const dataIndex = data.data.findIndex((row) => row.id === selectedRow.id);
			if (dataIndex !== -1) {
				newRowSelection[activeTab as TabId][dataIndex] = true;
			}
		});
		setRowSelection(newRowSelection);
	};

	const actions = useMemo(() => {
		return [
			...(tab === 'blocked' && verifyPermission(PERMISSIONS.adminUsersBlocklist)
				? [
						<Button
							onClick={() => (window.location.href = '/config/blocklist')}
							startIcon={<DoDisturbIcon />}
							variant="outlined"
							key="manage-blocklist"
						>
							Manage Blocklist
						</Button>,
					]
				: []),
			...(featureFlags?.USES_RFID_ENC
				? [
						<Button onClick={handleOpenReadBadgeDialog} startIcon={<BadgeIcon />} variant="outlined" key="read">
							Read Badge
						</Button>,
					]
				: []),
			<Button onClick={handleOpenScanDialog} key="scan">
				<QrCodeScannerIcon />
			</Button>,
			<Button onClick={handleOpenForm} startIcon={<AddIcon />} variant="outlined" key="add">
				Add User
			</Button>,
		];
	}, [handleOpenForm, featureFlags?.USES_RFID_ENC, tab, verifyPermission, handleOpenScanDialog]);

	const isLoading = authLoading || configLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<GenericDataPageHeader
				tabs={tabs}
				defaultTab="active"
				onTabChange={handleTabChange}
				actions={actions}
				downloadCSVReport={downloadCSVReport}
				menu={[
					{
						label: 'Merge Users',
						onClick: handleMergeUsers,
						icon: <CallMergeIcon />,
					},
				]}
			/>
			<div className="p-4 pt-0">
				{isSelectionMode && (
					<Alert
						severity="info"
						sx={{
							mt: 1,
							mb: 2,
							py: 1.5,
							'& .MuiAlert-message': {
								width: '100%',
								p: 0,
								display: 'flex',
								alignItems: 'center',
							},
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<Typography variant="body2" sx={{ mt: 0.25 }}>
								<strong>{selectedUsers.length}</strong> user(s) selected for merge.
								{selectedUsers.length >= 2 ? (
									<> Click "Merge Users" to proceed.</>
								) : (
									<> Select at least 2 users to merge.</>
								)}
							</Typography>
							<Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
								<Button size="medium" variant="outlined" onClick={handleCancelSelection} startIcon={<CloseIcon />}>
									Cancel
								</Button>
								{selectedUsers.length >= 2 && (
									<Button
										size="medium"
										variant="contained"
										onClick={() => setIsMergeDialogOpen(true)}
										startIcon={<CallMergeIcon />}
									>
										Merge Users
									</Button>
								)}
							</Box>
						</Box>
					</Alert>
				)}

				{/* Filter */}
				<div className="mt-2 flex flex-row flex-wrap gap-2">
					<div key="search" className="w-[60%] flex-grow flex gap-2">
						<FilterSearch
							label="Search User"
							defaultValue={search || ''}
							onChange={debounce((searchInput: string) => {
								handleSearchChange(searchInput);
							}, 350)}
							sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
						/>
						<FilterAutoCompleteById
							key="roleFilter"
							label="Filter Roles"
							options={roles}
							value={roleIds || []}
							onChange={(newRoleIds) => handleFilterChange({ roleIds: newRoleIds })}
							multiple
						/>
						<FilterAsyncAutoComplete
							key="groupFilter"
							name="groupIds"
							label="Filter Companies"
							dataSource="/api/data-sources/groups/data"
							value={groupIds || []}
							onChange={(newGroupIds) => handleFilterChange({ groupIds: newGroupIds })}
							multiple
						/>
						<FilterSelect
							key="roleStatus"
							listSubHeader="Filter Role Status"
							options={roleStatusTypes}
							value={roleStatus || 'all'}
							onChange={(newRoleStatus) => handleFilterChange({ roleStatus: newRoleStatus })}
							sx={{ width: '100%' }}
						/>
					</div>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						isLoading={usersLoading}
						data={data.data}
						columns={columns}
						rowCount={data.count || 0}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'lastUserActivityAt:desc')]}
						pageSize={data.pageSize}
						page={data.page}
						onRowClick={handleRowClick}
						pagination
						getRowClassName={(p: { row: User }) => (p.row.markedForReview ? 'error' : '')}
						onReload={revalidateUsers}
						enableRowSelection={isSelectionMode}
						rowSelection={rowSelection[activeTab as TabId]}
						onRowSelectionChange={handleRowSelectionChange}
					/>
				</Paper>
			</div>

			{isFormOpened && (
				<InviteForm
					isOpen={true}
					onClose={handleCloseForm}
					roles={inviteRoles}
					onInvite={handleInvite}
					authenticatedUser={authenticatedUser}
					defaultCountry={defaultCountry || 'BE'}
					permissions={{
						canCreateWithoutEmail: verifyPermission(PERMISSIONS.adminUsersCreate),
						isUserAdmin: verifyPermission(PERMISSIONS.adminUsersCreate),
						canImport: false,
					}}
				/>
			)}

			<ScanDialog isOpen={isScanDialogOpen} onClose={handleCloseScanDialog} onScan={handleScan} />

			<BadgeConfiguratorDialog.ReadBadge
				isOpen={isBadgeDialogOpen}
				tenantSecret={tenantSecret}
				onClose={handleCloseReadBadgeDialog}
			/>

			<MergeUsersDialog
				open={isMergeDialogOpen}
				onClose={() => {
					setIsMergeDialogOpen(false);
				}}
				selectedUsers={selectedUsers}
				onSuccess={handleMergeSuccess}
			/>
		</>
	);
}
